/**
 * Freshdesk knowledge-base URL classification + normalization.
 *
 * The single frontend source of truth for our two FP-facing Freshdesk portals:
 *   - ANZ -> evolutionplatform.poolwerx.com.au
 *   - USA -> evolutionplatform.poolwerx.com
 *
 * It classifies a URL's market, extracts the numeric article id, canonicalizes
 * a KB article URL (stripping Freshdesk's SEO slug + any query/anchor), and
 * produces a market-mismatch message. Centralized here so the reference-links
 * editor and any future surface share one ruleset.
 *
 * Backend twin: backend/shared/freshdesk/urls.py - keep the two in lockstep.
 *
 * Note the .com.au-contains-.com trap: market detection parses the hostname and
 * matches it for *exact equality* against the keyed map, never substring-matches
 * the raw string ('...poolwerx.com.au' contains 'poolwerx.com').
 */
import type { Market } from '../constants/markets'

// Full hostname -> market. Exact-equality lookup makes the .com.au / .com
// disambiguation automatic (no prefix/suffix matching anywhere). Null
// prototype so `host in FRESHDESK_PORTAL_HOSTS` (and a bare index) only ever
// matches our own keys - a plain object would report `Object.prototype` keys
// (`constructor`, `toString`, `hasOwnProperty`, ...) as Freshdesk hosts.
export const FRESHDESK_PORTAL_HOSTS: Record<string, Market> = Object.assign(
  Object.create(null) as Record<string, Market>,
  {
    'evolutionplatform.poolwerx.com.au': 'ANZ',
    'evolutionplatform.poolwerx.com': 'USA',
  },
)

// Matches a KB article path, with an optional leading locale segment (e.g.
// '/en' or '/en-us') and the trailing SEO slug Freshdesk appends (which can
// itself end in '-'). Applied to the URL pathname only.
const ARTICLE_PATH_RE =
  /^(\/[a-z]{2}(?:-[a-z]{2})?)?\/support\/solutions\/articles\/(\d+)(?:-.*)?\/?$/

function parse(raw: string | null | undefined): { host: string; url: URL } | null {
  if (!raw) return null
  try {
    const url = new URL(raw.trim())
    const host = url.hostname.toLowerCase().replace(/\.$/, '')
    if (!host) return null
    return { host, url }
  } catch {
    return null
  }
}

/** True when the URL's host is one of the FP-facing Freshdesk portals. */
export function isFreshdeskUrl(raw: string | null | undefined): boolean {
  const p = parse(raw)
  return p !== null && p.host in FRESHDESK_PORTAL_HOSTS
}

/** Market implied by the URL's Freshdesk portal host, or null. */
export function freshdeskMarketForUrl(raw: string | null | undefined): Market | null {
  const p = parse(raw)
  if (!p) return null
  return FRESHDESK_PORTAL_HOSTS[p.host] ?? null
}

/** The numeric KB article id (as a string), or null for non-article URLs. */
export function extractArticleId(raw: string | null | undefined): string | null {
  const p = parse(raw)
  if (!p || !(p.host in FRESHDESK_PORTAL_HOSTS)) return null
  const m = p.url.pathname.match(ARTICLE_PATH_RE)
  return m ? m[2] : null
}

/**
 * Canonicalize a Freshdesk KB *article* URL to
 * `scheme://host[/locale]/support/solutions/articles/<id>` - dropping the SEO
 * slug, query string and `#anchor` and lowercasing the host. Any non-article
 * Freshdesk URL and any non-Freshdesk URL is returned trimmed but otherwise
 * unchanged, so this is always safe to call.
 */
export function normalizeFreshdeskUrl(raw: string | null | undefined): string {
  const trimmed = (raw ?? '').trim()
  const p = parse(trimmed)
  if (!p || !(p.host in FRESHDESK_PORTAL_HOSTS)) return trimmed
  const m = p.url.pathname.match(ARTICLE_PATH_RE)
  if (!m) return trimmed
  const locale = m[1] ?? ''
  const articleId = m[2]
  const scheme = p.url.protocol.replace(/:$/, '') || 'https'
  return `${scheme}://${p.host}${locale}/support/solutions/articles/${articleId}`
}

export interface FreshdeskMarketMismatch {
  kind: 'mismatch' | 'nudge'
  message: string
}

/**
 * Compare a Freshdesk URL's implied market against a selected link market.
 *
 * Returns null when there's nothing to flag (not a Freshdesk URL, or markets
 * agree). Otherwise `kind` is:
 *  - 'mismatch' - a market is set and differs from the URL's domain (a hard
 *    warning: the FP would be sent to the wrong market's article).
 *  - 'nudge' - the link is 'All markets' but the URL is market-specific (a soft
 *    suggestion to scope it).
 */
export function freshdeskMarketMismatch(
  raw: string | null | undefined,
  market: Market | null,
): FreshdeskMarketMismatch | null {
  const urlMarket = freshdeskMarketForUrl(raw)
  if (urlMarket === null) return null
  if (market === null) {
    return {
      kind: 'nudge',
      message: `This looks like a ${urlMarket} Freshdesk link. Set the market to ${urlMarket}, or use a market-neutral link for "All markets".`,
    }
  }
  if (market !== urlMarket) {
    return {
      kind: 'mismatch',
      message: `This is a ${urlMarket} Freshdesk link but the market is set to ${market}.`,
    }
  }
  return null
}
